import { msg } from 'gt-react';
import { createRoute } from '@tanstack/react-router';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import { Route as pathlessAuthenticatedRoute } from '../../../route';
import { PricelistCaseDetailPage } from '../../PricelistCaseDetailPage';

const PRICELIST_CASE_CRUMB_FALLBACK = msg('Price change', {
  context: 'breadcrumb fallback when a price change number cannot be loaded',
});

const PRICELIST_MANAGEMENT_CRUMB = msg('Pricelist Management', {
  context: 'breadcrumb ancestor linking back to the pricelist list',
});

/**
 * `Pricelist Management > PC-2`.
 *
 * The parent crumb is declared rather than inherited: this route is top-level
 * (see above), so there is no ancestor match for the breadcrumb bar to read.
 * `crumbAncestors` exists for exactly this — a detail route that lives beside
 * its list rather than under it. Titles stay `msg`-encoded; the bar decodes
 * them in the active locale.
 */
const CRUMB_ANCESTORS = [
  {
    title: PRICELIST_MANAGEMENT_CRUMB,
    path: '/item-master/pricelist-management',
  },
];

/**
 * One pricelist update case (GEN-2524).
 *
 * A top-level route rather than a child of `/item-master/pricelist-management`
 * because that parent is still centrally declared in `src/routes.tsx` (it is
 * on the file-router burndown list), and a co-located child importing it would
 * make `routes.tsx` and this module import each other. The two pages link to
 * each other explicitly instead.
 *
 * Gated on `Subject.Product`, the same subject as the pricelist pages this
 * page is reached from: a case reorganizes files the caller can already read,
 * so it needs no grant of its own. The whole page — including the comments and
 * the timeline of its companion task — is authorized as the case, which is
 * what keeps `Subject.Task` out of it.
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/pricelist-cases/$caseId',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: PricelistCaseDetailPage,
  // Async so the crumb can name the case (`PC-2`) rather than its type. The
  // fetch is cached through the queryClient, so the page's own `get` query
  // reuses it instead of asking twice; a failure degrades to the type name and
  // still keeps the trail.
  loader: async ({ params, context }) => {
    try {
      const pricelistCase = await context.queryClient.fetchQuery(
        orpc.inventoryManagement.pricelistCases.get.queryOptions({
          input: { caseId: params.caseId },
        }),
      );
      return {
        crumbAncestors: CRUMB_ANCESTORS,
        crumb: pricelistCase.caseNumber ?? PRICELIST_CASE_CRUMB_FALLBACK,
      };
    } catch {
      return {
        crumbAncestors: CRUMB_ANCESTORS,
        crumb: PRICELIST_CASE_CRUMB_FALLBACK,
      };
    }
  },
});
